/* System defined core library */
import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import * as $ from 'jquery';

/* User defined library/components */
import { MetricApiService } from '../../scheduling/shared/services/metric-api.service';
import { OrchestrationApiService } from '../../scheduling/shared/services/orchestration-api.service';
import { SchedulingApiService } from '../../scheduling/shared/services/scheduling-api.service';
import { SelectedExamDetailService } from '../../scheduling/shared/services/selected-exam-detail.service';
import { DateTimeService } from './date-time.service';
import { TimerService } from './timer.service';
import { UtilityService } from './utility.service';

/* User defined models */
import { LpiData } from '../models/common/lpiData.model';
import { Dictionary } from '../models/common/shared.model';
import { AppointmentDetail } from '../models/scheduling/appointmentdetail.model';
import { Candidate } from '../models/scheduling/candidate.model';
import { Client } from '../models/scheduling/client.model';
import { Eligibility, EligibilityDetail } from '../models/scheduling/eligibility.model';
import { Exam } from '../models/scheduling/exam.model';
import { Program } from '../models/scheduling/program.model';
import { Session } from '../models/scheduling/session.model';
import { Timezone } from '../models/scheduling/timezone.model';

/* User defined enums/constants */
import { configSettings, TargetFlow, TermUrlConstants } from '../constants/scheduling.constant';
import { ComponentType, LogLevel } from '../enums/scheduling.enum';


@Injectable()
export class RoutingService {

  /* User defined private/public variables */
  public client: Client;
  public candidate: Candidate;
  public eligibility: Eligibility;
  public eligibilityDetails: EligibilityDetail[];
  public exam: Exam;
  public lpiData: LpiData;
  public program: Program;
  public selectedAppointments: AppointmentDetail[];
  public timezone: Timezone;
  public lpiCandidate: Candidate;

  /* Service subscriptions */
  public client$: Subscription;
  public candidate$: Subscription;
  public eligibility$: Subscription;
  public eligibilityDetails$: Subscription;
  public exam$: Subscription;
  public lpiData$: Subscription;
  public program$: Subscription;
  public selectedAppointments$: Subscription;
  public timezone$: Subscription;
  public lpiCandidate$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private location: Location,
    private metricApiService: MetricApiService,
    private orchestrationApiService: OrchestrationApiService,
    private router: Router,
    private schedulingApiService: SchedulingApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private timerService: TimerService
  ) {
    this.client$ = selectedExamDetailService.client.subscribe(
      client => {
        this.client = client;
      });

    this.candidate$ = selectedExamDetailService.candidate.subscribe(
      candidate => {
        this.candidate = candidate;
      });

    this.eligibility$ = selectedExamDetailService.eligibility.subscribe(
      eligibility => {
        this.eligibility = eligibility;
      });

    this.eligibilityDetails$ = selectedExamDetailService.eligibilityDetails.subscribe(
      eligibilityDetails => {
        this.eligibilityDetails = eligibilityDetails;
      });

    this.exam$ = selectedExamDetailService.exam.subscribe(
      exam => {
        this.exam = exam;
      });

    this.lpiData$ = selectedExamDetailService.lpiData.subscribe(
      lpiData => {
        this.lpiData = lpiData;
      });

    this.program$ = selectedExamDetailService.program.subscribe(
      program => {
        this.program = program;
      });

    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointments => {
        this.selectedAppointments = selectedAppointments;
      });

    this.timezone$ = selectedExamDetailService.timezone.subscribe(
      timezone => {
        this.timezone = timezone;
      });

    this.lpiCandidate$ = selectedExamDetailService.lpiCandidate.subscribe(
      lpiCandidate => {
        this.lpiCandidate = lpiCandidate;
      });
  }

  // Search Nav
  public searchFwdNavigation(componentType: ComponentType) {
    if (this.selectedExamDetailService.isFlowComplete) {
      this.metricApiService.log(LogLevel.Debug, `Candidate used next button in search availability flow with completed session data. Directed to homepage.`);
      // this.homeNavigation();
    }
    switch (componentType) {
      case ComponentType.FindAvailabilityComponent: {
        this.getFindAvailabilityComponent().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }

      case ComponentType.FindAvailabilityLocationComponent: {
        this.getFindAvailabilityLocationComponent().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }

      case ComponentType.FindAvailabilityTimezoneComponent: {
        this.getFindAvailabilityTimezoneComponent().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }
    }
  }

  private getFindAvailabilityComponent(): Observable<string> {
    if (this.selectedExamDetailService.isProctored && !this.isProctorable(this.selectedAppointments)) {
      this.metricApiService.log(LogLevel.Debug, 'User Attempted to find proctor availibility for non-proctor exam as proctor: User Redirected to error page.');
      return this.getInvalidNavigation();
    }

    return new Observable((observer: any) => {
      if (this.selectedExamDetailService.isProctored) {
        observer.next('/scheduling/findAvailabilityTimezone');
      } else {
        observer.next('/scheduling/findAvailabilityLocation');
      }
      observer.complete();
    });
  }

  private getFindAvailabilityLocationComponent(): Observable<string> {
    return new Observable((observer: any) => {
      observer.next('/scheduling/findAvailabilityTestcenter');
      observer.complete();
    });
  }

  private getFindAvailabilityTimezoneComponent(): Observable<string> {
    return new Observable((observer: any) => {
      observer.next('/scheduling/findAvailabilityProctor');
      observer.complete();
    });
  }

  // Schedule Nav
  public scheduleFwdNavigation(componentType: ComponentType) {
    if (this.selectedExamDetailService.isFlowComplete) {
      this.metricApiService.log(LogLevel.Debug, `Candidate used next button in schedule flow with completed session data. Directed to homepage.`);
      // this.homeNavigation();
    }
    switch (componentType) {
      case ComponentType.ProgramInformationComponent: {
        this.getProgramInfoFwdNavigation().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }

      case ComponentType.CustomAgreementComponent: {
        this.getCustomAgreementFwdNavigation().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }

      case ComponentType.AgreementComponent: {
        this.getAgreementFwdNavigation().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }

      case ComponentType.EligibilitySelectorComponent: {
        this.getEligibilityFwdNavigation().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }

      case ComponentType.ScheduleLocationComponent: {
        this.getScheduleLocationFwdNavigation().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }

      case ComponentType.ScheduleTimezoneComponent: {
        this.getScheduleTimezoneFwdNavigation().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }

      case ComponentType.ScheduleTestCenterComponent:
      case ComponentType.ScheduleProctorComponent:
      case ComponentType.MultidayTestCenterComponent: {
        this.getScheduleSlotFwdNavigation().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }

      case ComponentType.BiometricConsentComponent: {
        this.getScheduleBiometricFwdNavigation().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }

      case ComponentType.CandidateProfileComponent: {
        this.getCandidateProfileFwdNavigation().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }

      case ComponentType.ConfirmationComponent: {
        this.getScheduleConfirmationFwdNavigation().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }

      case ComponentType.PaymentComponent: {
        this.getSchedulePaymentFwdNavigation().subscribe((route: string) => {
          if (!!route) {
            this.router.navigate([route]);
          }
        });
        break;
      }
    }
  }

  public scheduleRedirectToSlotSelection() {
    if (this.selectedExamDetailService.isProctored) {
      this.router.navigate(['/scheduling/scheduleProctor']);
    } else {
      if (this.selectedAppointments &&
        this.selectedAppointments[0].exam.sessions &&
        this.selectedAppointments[0].exam.sessions.length > 1
      ) {
        this.router.navigate(['/scheduling/scheduleMultiday']);
      } else {
        this.router.navigate(['/scheduling/scheduleTestcenter']);
      }
    }
  }

  private getProgramInfoFwdNavigation(): Observable<string> {

    if (this.selectedExamDetailService.isCustomAgreementRequired) {
      return new Observable((observer: any) => {
        observer.next('/scheduling/customAgreement');
        observer.complete();
      });
    }

    return this.getCustomAgreementFwdNavigation();
  }

  private getCustomAgreementFwdNavigation(): Observable<string> {
    if (!this.selectedExamDetailService.skipPrivacyPage) {
      return new Observable((observer: any) => {
        observer.next('/scheduling/agreement');
        observer.complete();
      });
    }

    return this.getAgreementFwdNavigation();
  }

  private getAgreementFwdNavigation(): Observable<string> {
    if (!this.selectedExamDetailService.skipExamSelection && !this.selectedExamDetailService.invalidEligibilityRequest) {
      return new Observable((observer: any) => {
        observer.next('/scheduling/eligibility');
        observer.complete();
      });
    }

    if (!!this.selectedExamDetailService.invalidEligibilityRequest) {
      this.metricApiService.log(LogLevel.Debug, 'Invalid Eligibility Request: User Redirected to error page.');
      return this.getInvalidNavigation();
    }

    return this.getEligibilityFwdNavigation();
  }

  private getEligibilityFwdNavigation(): Observable<string> {

    if (this.selectedExamDetailService.isProctored ? !this.isProctorable(this.selectedAppointments) : !this.isBrickAndMortarAvailible(this.selectedAppointments)) {
      this.metricApiService.log(LogLevel.Debug, 'User Attempted to schedule non-proctor exam as proctor: User Redirected to error page.');
      this.selectedExamDetailService.invalidEligibilityRequest = 'InvalidModality';
      return this.getInvalidNavigation();
    }

    return new Observable((observer: any) => {

      this.orchestrationApiService.getConfiguration(
        [configSettings.SkipProfile, configSettings.SkipMetrics],
        this.client.code
      )
        .subscribe((response: any) => {
          this.selectedExamDetailService.skipMetrics = response[configSettings.SkipMetrics] === 'True';
          this.selectedExamDetailService.skipProfileConfig = response[configSettings.SkipProfile] === 'True';
        });

      this.selectedExamDetailService.setOverlap(this.selectedAppointments);
      this.orchestrationApiService.getCandidateSelectableTA(this.client, this.program, this.selectedAppointments).subscribe((response: any) => {

        this.selectedExamDetailService.showCandidateSelectableView = response['showCandidateSelectableView'];
        this.selectedExamDetailService.setPaymentOptions(response['paymentOptions']);
        this.selectedExamDetailService.selectedAppointmentIndex = 0;
        this.selectedExamDetailService.appointmentCountForInfo.next(0);

        this.prefillCandidateInfo();

        if (response['showCandidateSelectableView']) {
          observer.next('/scheduling/candidateSelectable');
          observer.complete();
        }

        if (this.selectedExamDetailService.isProctored) {
          observer.next('/scheduling/scheduleTimeZone');
          observer.complete();
        }
        observer.next('/scheduling/scheduleLocation');
        observer.complete();
      });
    });
  }

  private getScheduleLocationFwdNavigation(): Observable<string> {
    return new Observable((observer: any) => {
      if (this.selectedAppointments[0].exam.sessions && this.selectedAppointments[0].exam.sessions.length > 1) {
        observer.next('/scheduling/scheduleMultiday');
      } else {
        observer.next('/scheduling/scheduleTestcenter');
      }
      observer.complete();
    });
  }

  private getScheduleTimezoneFwdNavigation(): Observable<string> {
    return new Observable((observer: any) => {
      observer.next('/scheduling/scheduleProctor');
      observer.complete();
    });
  }

  private getScheduleSlotFwdNavigation(): Observable<string> {
    this.startTimer();

    if (this.selectedExamDetailService.isBiometricConsentRequiredSchedule) {
      return new Observable((observer: any) => {
        observer.next('/scheduling/biometricConsent');
        observer.complete();
      });
    }

    return this.getScheduleBiometricFwdNavigation();
  }

  private getScheduleBiometricFwdNavigation(): Observable<string> {
    if (!this.selectedExamDetailService.canSkipProfile) {
      return new Observable((observer: any) => {
        observer.next('/scheduling/candidateProfile');
        observer.complete();
      });
    }

    return this.getCandidateProfileFwdNavigation();
  }

  private getCandidateProfileFwdNavigation(): Observable<string> {
    return new Observable((observer: any) => {
      observer.next('/scheduling/confirmation');
      observer.complete();
    });
  }

  private getScheduleConfirmationFwdNavigation(): Observable<string> {
    return new Observable((observer: any) => {
      observer.next('/scheduling/payment');
      observer.complete();
    });
  }

  private getSchedulePaymentFwdNavigation(): Observable<string> {
    return new Observable((observer: any) => {
      if (this.selectedExamDetailService.skipConfirmPage) {
        this.navigateToTerm(TermUrlConstants.Scheduled);
        observer.next('');
      } else {
        observer.next('/scheduling/confirmation');
      }
      observer.complete();
    });
  }

  // Reschedule Nav
  public rescheduleFwdNavigation(componentType: ComponentType) {
    if (this.selectedExamDetailService.isFlowComplete) {
      this.metricApiService.log(LogLevel.Debug, `Candidate used next button in reschedule flow with completed session data. Directed to homepage.`);
      // this.homeNavigation();
    }
    switch (componentType) {
      case ComponentType.RescheduleComponent:
      case ComponentType.AppointmentSearchComponent: {
        this.getRescheduleFwdNavigation().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }

      case ComponentType.RescheduleTimezoneComponent: {
        this.getRescheduleTimezoneFwdNavigation().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }

      case ComponentType.RescheduleLocationComponent: {
        this.getRescheduleLocationFwdNavigation().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }

      case ComponentType.RescheduleTestCenterComponent:
      case ComponentType.RescheduleProctorComponent:
      case ComponentType.RescheduleMultidayTestCenterComponent: {
        this.getRescheduleSlotFwdNavigation().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }

      case ComponentType.BiometricConsentComponent: {
        this.getRescheduleBiometricFwdNavigation().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }

      case ComponentType.ReconfirmationComponent: {
        this.getRescheduleConfirmationNavigation().subscribe((route: string) => {
          if (!!route) {
            this.router.navigate([route]);
          }
        });
        break;
      }

      case ComponentType.PaymentComponent: {
        this.getReschedulePaymentFwdNavigation().subscribe((route: string) => {
          if (!!route) {
            this.router.navigate([route]);
          }
        });
        break;
      }
    }
  }

  public rescheduleRedirectToSlotSelection() {
    if (this.selectedExamDetailService.isProctored) {
      this.router.navigate(['/scheduling/rescheduleappointment/rescheduleProctor']);
    } else {
      if (this.selectedAppointments &&
        this.selectedAppointments[0].exam.sessions &&
        this.selectedAppointments[0].exam.sessions.length > 1
      ) {
        this.router.navigate(['/scheduling/rescheduleappointment/rescheduleMultiday']);
      } else {
        this.router.navigate(['/scheduling/rescheduleappointment/rescheduleTestcenter']);
      }
    }
  }

  private getRescheduleFwdNavigation(): Observable<string> {
    this.selectedExamDetailService.currentTargetFlow = TargetFlow.Reschedule;

    if (this.selectedExamDetailService.isProctored) {
      if (this.isProctorable(this.selectedAppointments)) {
        return new Observable((observer: any) => {
          observer.next('/scheduling/rescheduleappointment/rescheduleTimeZone');
          observer.complete();
        });
      } else {
        this.metricApiService.log(LogLevel.Debug, 'User Attempted to schedule non-proctor exam as proctor: User Redirected to error page.');
        return this.getInvalidNavigation();
      }
    }

    let sessions = this.selectedAppointments[0].exam.sessions;

    if (sessions && sessions.length > 1) {
      if (!sessions.every(s => s.selected)) {
        return new Observable((observer: any) => {
          observer.next('/scheduling/rescheduleappointment/rescheduleSession');
          observer.complete();
        });
      }
    }

    return new Observable((observer: any) => {
      observer.next('/scheduling/rescheduleappointment/rescheduleLocation');
      observer.complete();
    });
  }

  private getRescheduleTimezoneFwdNavigation(): Observable<string> {
    return new Observable((observer: any) => {
      observer.next('/scheduling/rescheduleappointment/rescheduleProctor');
      observer.complete();
    });
  }

  private getRescheduleLocationFwdNavigation(): Observable<string> {
    return new Observable((observer: any) => {
      if (this.selectedAppointments[0].exam.sessions && this.selectedAppointments[0].exam.sessions.length > 1) {
        observer.next('/scheduling/rescheduleappointment/rescheduleMultiday');
      } else {
        observer.next('/scheduling/rescheduleappointment/rescheduleTestcenter');
      }
      observer.complete();
    });
  }

  private getRescheduleSlotFwdNavigation(): Observable<string> {
    this.startTimer();

    if (this.selectedExamDetailService.isBiometricConsentRequiredReschedule) {
      return new Observable((observer: any) => {
        observer.next('/scheduling/biometricConsent');
        observer.complete();
      });
    }

    return this.getRescheduleBiometricFwdNavigation();
  }

  private getRescheduleBiometricFwdNavigation(): Observable<string> {
    return new Observable((observer: any) => {
      observer.next('/scheduling/rescheduleappointment/reconfirmation');
      observer.complete();
    });
  }

  private getRescheduleConfirmationNavigation(): Observable<string> {
    if (this.selectedExamDetailService.isPaymentRequired) {
      return new Observable((observer: any) => {
        observer.next('/scheduling/rescheduleappointment/payment');
        observer.complete();
      });
    }

    return this.getReschedulePaymentFwdNavigation();
  }

  private getReschedulePaymentFwdNavigation(): Observable<string> {
    return new Observable((observer: any) => {
      if (this.selectedExamDetailService.skipConfirmPage) {
        this.navigateToTerm(TermUrlConstants.Rescheduled);
        observer.next('');
      } else {
        observer.next('/scheduling/rescheduleappointment/reconfirmation');
      }
      observer.complete();
    });
  }

  // Cancel Nav
  public cancelFwdNavigation(componentType: ComponentType) {
    if (this.selectedExamDetailService.isFlowComplete) {
      this.metricApiService.log(LogLevel.Debug, `Candidate used next button in cancel flow with completed session data. Directed to homepage.`);
      // this.homeNavigation();
    }
    switch (componentType) {
      case ComponentType.CancellationComponent:
      case ComponentType.AppointmentSearchComponent: {
        this.getCancelFwdNavigation().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }

      case ComponentType.CancellationVerificationComponent: {
        this.getCancelVerificationFwdNavigation().subscribe((route: string) => {
          if (!!route) {
            this.router.navigate([route]);
          }
        });
        break;
      }

      case ComponentType.PaymentCancellationComponent: {
        this.getCancelPaymentFwdNavigation().subscribe((route: string) => {
          if (!!route) {
            this.router.navigate([route]);
          }
        });
        break;
      }
    }
  }

  private getCancelFwdNavigation(): Observable<string> {
    this.selectedExamDetailService.currentTargetFlow = TargetFlow.Cancel;
    return new Observable((observer: any) => {
      observer.next('/scheduling/cancelappointment/verification');
      observer.complete();
    });
  }

  private getCancelVerificationFwdNavigation(): Observable<string> {
    if (this.selectedExamDetailService.isPaymentRequired) {
      return new Observable((observer: any) => {
        observer.next('/scheduling/cancelappointment/payment');
        observer.complete();
      });
    } else {
      return this.getCancelPaymentFwdNavigation();
    }
  }

  private getCancelPaymentFwdNavigation(): Observable<string> {
    return new Observable((observer: any) => {
      if (this.selectedExamDetailService.skipConfirmPage) {
        this.navigateToTerm(TermUrlConstants.Canceled);
        observer.next('');
      } else {
        observer.next('/scheduling/cancelappointment/confirmation');
      }
      observer.complete();
    });
  }

  // Home page
  public homeNavigation() {
    this.getHomeNavigation().subscribe((route: string) => {
      this.router.navigate([route]);
    });
  }

  private getHomeNavigation() {
    return new Observable((observer: any) => {
      this.selectedExamDetailService.resetExamService();
      observer.next('/home');
      observer.complete();
    });
  }

  // Invalid request page
  public invalidNavigation() {
    this.getInvalidNavigation().subscribe((route: string) => {
      this.router.navigate([route]);
    });
  }

  private getInvalidNavigation(): Observable<string> {
    return new Observable((observer: any) => {
      this.selectedExamDetailService.setClient(null);
      this.selectedExamDetailService.setProgram(null);
      this.selectedExamDetailService.setEligibility(null);
      this.selectedExamDetailService.SetAppointmentDetail(null);
      observer.next('/scheduling/invalidRequest');
      observer.complete();
    });
  }

  // Back - Search
  public backwardNavigationSearch(componentType: ComponentType) {
    if (this.selectedExamDetailService.isFlowComplete) {
      this.metricApiService.log(LogLevel.Debug, `Candidate used back button in search availability flow with completed session data. Directed to homepage.`);
      // this.homeNavigation();
    }
    switch (componentType) {
      case ComponentType.FindAvailabilityComponent: {
        this.getHomeNavigation().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }
      default: {
        this.location.back();
      }
    }
  }

  // Back - ReSchedule
  public backwardNavigation(componentType: ComponentType) {
    if (this.selectedExamDetailService.isFlowComplete) {
      this.metricApiService.log(LogLevel.Debug, `Candidate used back button in reschedule flow with completed session data. Directed to homepage.`);
      // this.homeNavigation();
    }
    switch (componentType) {
      case ComponentType.RescheduleComponent: {
        this.getHomeNavigation().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }
      case ComponentType.ScheduleProctorComponent: {
        this.goBackFromScheduleProctor().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }
      case ComponentType.RescheduleProctorComponent: {
        this.goBackFromRescheduleProctor().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }
      case ComponentType.ScheduleTimezoneComponent: {
        this.goBackFromScheduleTimezone().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }
      case ComponentType.RescheduleTimezoneComponent: {
        this.goBackFromRescheduleTimezone().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }
      default: {
        this.selectedExamDetailService.setIsUserNavigating();
        this.location.back();
      }
    }
  }

  // Back - Schedule
  public scheduleBwdNavigation(componentType: ComponentType) {
    if (this.selectedExamDetailService.isFlowComplete) {
      this.metricApiService.log(LogLevel.Debug, `Candidate used back button in schedule flow with completed session data. Directed to homepage.`);
      // this.homeNavigation();
    }
    switch (componentType) {
      case ComponentType.ScheduleLocationComponent: {
        this.goBackfromScheduleLocation().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }
      case ComponentType.EligibilitySelectorComponent: {
        this.goBackFromEligibilitySelection().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }
      case ComponentType.CustomAgreementComponent: {
        this.goBackFromCustomAgreement().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }
      case ComponentType.AgreementComponent: {
        this.goBackFromAgreement().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }
      case ComponentType.CandidateSelectableComponent: {
        this.goBackFromCandidateSelectable().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }
      case ComponentType.MultidayTestCenterComponent:
      case ComponentType.ScheduleTestCenterComponent: {
        this.goBackfromMultidayTestCenter().subscribe((route: string) => {
          this.router.navigate([route]);
        });
        break;
      }
      default: {
        this.selectedExamDetailService.setIsUserNavigating();
        this.location.back();
      }
    }
  }

  private goBackfromScheduleLocation() {
    if (this.selectedExamDetailService.showCandidateSelectableView) {
      return new Observable((observer: any) => {
        observer.next('/scheduling/candidateSelectable');
        observer.complete();
      });
    }

    return this.goBackFromCandidateSelectable();
  }

  private goBackFromCandidateSelectable(): Observable<string> {
    if (!this.selectedExamDetailService.skipExamSelection) {
      return new Observable((observer: any) => {
        observer.next('/scheduling/eligibility');
        observer.complete();
      });
    }

    return this.goBackFromEligibilitySelection();
  }

  private goBackFromEligibilitySelection(): Observable<string> {
    if (!this.selectedExamDetailService.skipPrivacyPage) {
      return new Observable((observer: any) => {
        observer.next('/scheduling/agreement');
        observer.complete();
      });
    }
    return this.goBackFromAgreement();
  }

  private goBackFromCustomAgreement(): Observable<string> {
    return new Observable((observer: any) => {
      observer.next('/scheduling/testInformation');
      observer.complete();
    });
  }

  private goBackFromAgreement(): Observable<string> {
    if (this.selectedExamDetailService.isCustomAgreementRequired) {
      return new Observable((observer: any) => {
        observer.next('/scheduling/customAgreement');
        observer.complete();
      });
    }
    return this.goBackFromCustomAgreement();
  }

  private goBackfromMultidayTestCenter() {
    return new Observable((observer: any) => {
      observer.next('/scheduling/scheduleLocation');
      observer.complete();
    });
  }

  private goBackFromScheduleProctor() {
    return new Observable((observer: any) => {
      observer.next('/scheduling/scheduleTimeZone');
      observer.complete();
    });
  }

  private goBackFromRescheduleProctor() {
    return new Observable((observer: any) => {
      observer.next('/scheduling/rescheduleappointment/rescheduleTimeZone');
      observer.complete();
    });
  }

  private goBackFromScheduleTimezone(): Observable<string> {
    if (this.selectedExamDetailService.showCandidateSelectableView) {
      return new Observable((observer: any) => {
        observer.next('/scheduling/candidateSelectable');
        observer.complete();
      });
    }
    return this.goBackFromCandidateSelectable();
  }

  private goBackFromRescheduleTimezone() {
    return new Observable((observer: any) => {
      observer.next('/scheduling/rescheduleappointment/reschedule');
      observer.complete();
    });
  }

  // External Navigation
  public navigateAwayFromProscheduler() {
    if (this.selectedExamDetailService.terminationUrl) {
      this.navigateToTerm();
    } else {
      this.navigateToPrometric();
    }
  }

  // Term URL
  public navigateToTerm(outcome?: string) {
    let termUrl: string = (this.selectedExamDetailService.terminationUrl && (this.selectedExamDetailService.terminationUrl.indexOf('http') > -1)) ?
      this.selectedExamDetailService.terminationUrl :
      'https://' + this.selectedExamDetailService.terminationUrl;

    if (this.selectedExamDetailService.skipConfirmPage && !!outcome) {
      let apptdata: string = '';

      apptdata += '<appointmentData>';
      apptdata += '<appointments>';

      if (outcome === TermUrlConstants.PolicyDeclined) {
        apptdata += '<appointment';
        apptdata += ` outcome="${outcome}"`;

        let examName: string = (this.exam && this.exam.code) ? this.exam.code : '';
        apptdata += ` examName="${examName}"`;
        apptdata += '/>';
      } else {
        this.selectedAppointments.forEach((appointment: AppointmentDetail) => {
          if (!!appointment.exam.sessions) {
            appointment.exam.sessions.forEach((session: Session) => {
              if (session.slot.duration) {
                apptdata += '<appointment';
                apptdata += ` outcome="${outcome}"`;

                apptdata += ` siteCode="${session.site ? session.site.id : this.selectedExamDetailService.proctorSiteId}"`;
                apptdata += ` confirmationNumber="${session.confirmationNumber}"`;

                let startDate: Date = new Date(session.slot.startDateTime);
                let endDate: Date = UtilityService.addMins(startDate, session.slot.duration);

                apptdata += ` startDateTime="${DateTimeService.formatDateTimeForPost(startDate)}"`;
                apptdata += ` endDateTime="${DateTimeService.formatDateTimeForPost(endDate)}"`;
                apptdata += ` examName="${appointment.exam.code}"`;
                apptdata += '/>';
              }
            });
          } else {
            apptdata += '<appointment';
            apptdata += ` outcome="${outcome}"`;

            apptdata += ` siteCode="${appointment.appointmentSlot.site ? appointment.appointmentSlot.site.id : this.selectedExamDetailService.proctorSiteId}"`;
            apptdata += ` confirmationNumber="${appointment.ConfirmationNumber}"`;

            const startDate: Date = new Date(appointment.appointmentSlot.startDateTime);
            const endDate: Date = UtilityService.addMins(startDate, appointment.appointmentSlot.duration);

            const tz = !!this.timezone ? this.timezone : appointment.appointmentSlot.proctorTimeZone;

            apptdata += ` startDateTime="${DateTimeService.formatDateTimeForPost(startDate)}"`;
            apptdata += ` endDateTime="${DateTimeService.formatDateTimeForPost(endDate)}"`;
            if (!!tz && !!tz.name) {
              apptdata += ` timeZone="${tz.name}"`;
            }
            apptdata += ` examName="${appointment.exam.code}"`;
            apptdata += '/>';
          }
        });
      }

      apptdata += '</appointments>';
      apptdata += '</appointmentData>';

      const form = $(`<form action='${termUrl}' method='POST'> <input name='apptdata' value='${apptdata}' type='hidden' /> </form>`);
      form.appendTo('body').submit();
    } else {
      window.open(termUrl, '_self');
    }
  }

  // Navigate to prometric website(in case of no Term URL)
  public navigateToPrometric() {
    window.open('https://prometric.com/', '_self');
  }

  public navigateToUrl(url: string) {
    window.open(url, '_self');
  }

  // Utility
  private prefillCandidateInfo() {

    if (!this.eligibility && !!this.lpiData && !!this.lpiData.eligibility) {
      let tempElig = new Eligibility();
      tempElig.eligibilityDetails = new Dictionary();
      this.lpiData.eligibility.eligibilityDetails._keys.forEach((key) => {
        tempElig.eligibilityDetails.add(key, this.lpiData.eligibility.eligibilityDetails[key]);
      });
      this.selectedExamDetailService.setEligibility(tempElig);
    }

    let tempCandidate: Candidate = new Candidate();
    let selectedExam: any = this.selectedAppointments[0].exam;

    // if there's a candidate already defined populate the candidateDetails.
    if (!this.candidate && this.selectedExamDetailService.lpiCandidateGuid) {
      this.retriveCandidateData(selectedExam);
    }

    if (this.lpiCandidate && !this.eligibilityDetails) {
      this.selectedExamDetailService.SetCandidateDataSource(this.lpiCandidate);
    }

    if (this.eligibilityDetails) {
      tempCandidate.defaultDetails = new Dictionary();

      selectedExam = this.eligibilityDetails.find((elig: EligibilityDetail) => {
        return elig.exam.id === this.selectedAppointments[0].exam.id;
      });
      // if there are multiple selected appts pick first exam
      selectedExam = selectedExam || this.eligibilityDetails[0];

      if (!!this.candidate) {
        this.eligibility.eligibilityDetails.keys().forEach((key) => {
          tempCandidate.defaultDetails.add(key.toString().toLowerCase(), this.eligibility.eligibilityDetails[key]);
        });
        if (tempCandidate.defaultDetails._keys.length > 0) {
          this.selectedExamDetailService.SetCandidateDataSource(tempCandidate);
        }
      }

      if (!!selectedExam.demographics) {
        for (var key in selectedExam.demographics) {

          let demographicValue = selectedExam.demographics[key] || (this.lpiCandidate ? this.lpiCandidate.defaultDetails[key.toString().toLowerCase()] : null);
          tempCandidate.defaultDetails.add(key.toString().toLowerCase(), demographicValue);

          if (key === 'country') {
            const countryCode = selectedExam.demographics['country'] || (this.lpiCandidate ? this.lpiCandidate.defaultDetails['country']: null);
            if (countryCode) {
              this.schedulingApiService.setCountryInfo(tempCandidate, countryCode.toLowerCase());
            }
          }
        }
      }

      // HACK: TODO: prefill elig# based on eligibility id
      if (this.eligibility && this.eligibility.eligibilityDetails && this.eligibility.eligibilityDetails['ELIGIBILITY_NUMBER']) {
        tempCandidate.defaultDetails.add('Eligibility #'.toLowerCase(), this.eligibility.eligibilityDetails['ELIGIBILITY_NUMBER']);
      }

      if (tempCandidate.defaultDetails._keys.length > 0) {
        this.selectedExamDetailService.SetCandidateDataSource(tempCandidate);
      }
    }
  }

  /**
   * retrieve candidateInfo based on candidateGuid
   * @param selectedExam
   */
  private retriveCandidateData(selectedExam: Exam) {

    this.schedulingApiService.getPersonalDetail(
      this.selectedExamDetailService.lpiCandidateGuid,
      this.client.code,
      this.program.code,
      selectedExam.code).subscribe(candidate => {
      });
  }

  private isProctorable(selectedAppointments: AppointmentDetail[]) {
    return selectedAppointments.every(apt => {
      return apt.exam.proctorScheduling;
    });
  }

  private isBrickAndMortarAvailible(selectedAppointments: AppointmentDetail[]) {
    return selectedAppointments.every(apt => {
      return apt.exam.nonProctorScheduling;
    });
  }

  private startTimer() {
    const clock = document.getElementById('timer');
    // innerHTML may be empty -OR- filled with whitespace when not displaying text
    if (!clock.innerHTML.trim()) {
      this.timerService.startTimer();
    }
  }
}
